import React from 'react';
import { Alert, Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import type { VendorApiOperationKey } from '../../types/vendor.types';
import {
    VENDOR_API_OPERATION_COUNT,
    VENDOR_API_OPERATION_GROUPS,
    toggleOperation,
    withGroup,
    withoutGroup,
} from './apiAccessOperations';

export type PickerTranslate = (key: string, options?: Record<string, unknown>) => string;

interface ApiOperationsPickerProps {
    value: VendorApiOperationKey[];
    onChange: (next: VendorApiOperationKey[]) => void;
    translate: PickerTranslate;
    disabled?: boolean;
}

export const ApiOperationsPicker = ({ value, onChange, translate, disabled = false }: ApiOperationsPickerProps) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {VENDOR_API_OPERATION_GROUPS.map((group) => (
            <Box
                key={group.key}
                data-testid={`api-group-${group.key}`}
                sx={{ border: '1px solid #E0E0E0', borderRadius: 2, p: 2 }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {translate(`resources.vendors.apiAccess.groups.${group.key}`)}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button size="small" disabled={disabled} onClick={() => onChange(withGroup(value, group.key))}>
                            {translate('resources.vendors.apiAccess.selectAll')}
                        </Button>
                        <Button size="small" disabled={disabled} onClick={() => onChange(withoutGroup(value, group.key))}>
                            {translate('resources.vendors.apiAccess.clear')}
                        </Button>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {group.operations.map((operation) => (
                        <FormControlLabel
                            key={operation.key}
                            disabled={disabled}
                            sx={{ alignItems: 'flex-start', mb: 0.5, '& .MuiCheckbox-root': { pt: 0.5 } }}
                            control={
                                <Checkbox
                                    checked={value.includes(operation.key)}
                                    onChange={() => onChange(toggleOperation(value, operation.key))}
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body2">{translate(operation.labelKey)}</Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                        {`${operation.method} ${operation.path}`}
                                    </Typography>
                                </Box>
                            }
                        />
                    ))}
                </Box>
            </Box>
        ))}
        <Typography variant="caption" color="text.secondary">
            {translate('resources.vendors.apiAccess.enabledCount', { count: value.length, total: VENDOR_API_OPERATION_COUNT })}
        </Typography>
        {value.length === 0 && (
            <Alert severity="warning">{translate('resources.vendors.apiAccess.noneEnabledWarning')}</Alert>
        )}
    </Box>
);
